<template>
  <div class="page-wrap">

    <!-- No data state -->
    <template v-if="!store.hasData">
      <div class="state-no-data">
        <div class="nd-icon">📋</div>
        <h2>ยังไม่มีข้อมูล</h2>
        <p>กรุณาอัปโหลดไฟล์ Excel ก่อนเพื่อให้ระบบประมวลผลข้อมูลลูกค้า</p>
        <a class="btn-upload" href="#" @click.prevent="router.push({ name: 'CsUpload' })">ไปหน้าอัปโหลด</a>
      </div>
    </template>

    <template v-else>
      <!-- Filter bar (2 rows) -->
      <div class="filter-bar">
        <!-- Row 1: metric mode -->
        <div class="filter-row">
          <span class="filter-label">แสดงผล</span>
          <button
v-for="m in modes" :key="m.key"
            :class="['mode-btn', m.colorClass, mode === m.key ? 'active' : '']"
            @click="mode = m.key">
            {{ m.label }}
          </button>
        </div>
        <!-- Row 2: transaction type filter (shown only when the current mode supports it) -->
        <div v-if="!isTypeFilterDisabled" class="filter-row filter-row-type">
          <span class="filter-label">ประเภทตั๋ว</span>
          <button
v-for="f in typeFilters" :key="f.key"
            :class="['mode-btn', f.colorClass, txnType === f.key ? 'active' : '']"
            @click="txnType = f.key">
            {{ f.label }}
          </button>
        </div>

        <!-- Row 3: year filter (yearable modes only) -->
        <div v-if="isYearableMode" class="filter-row filter-row-type">
          <span class="filter-label">ช่วงเวลา</span>
          <button
:class="['mode-btn', 'year-btn', yearFilter === null ? 'active' : '']"
            @click="yearFilter = null">ทั้งหมด</button>
          <button
v-for="y in availableYears" :key="y"
            :class="['mode-btn', 'year-btn', yearFilter === y ? 'active' : '']"
            @click="yearFilter = y">
            ปี {{ y }}
          </button>
        </div>

        <!-- Row 4: tier filter (hidden for interest-ratio which is per-ticket) -->
        <div v-if="mode !== 'interest-ratio'" class="filter-row filter-row-type">
          <span class="filter-label">ระดับ</span>
          <button
:class="['mode-btn', 'tier-btn', tierFilter === 'all' ? 'active' : '']"
            @click="tierFilter = 'all'">ทั้งหมด</button>
          <button
v-for="t in tierOptions" :key="t.key"
            :class="['mode-btn', 'tier-btn', t.cssKey, tierFilter === t.key ? 'active' : '']"
            @click="tierFilter = t.key">
            {{ t.label }}
          </button>
        </div>
      </div>

      <!-- Section card -->
      <div class="section-card">
        <!-- Section header -->
        <div class="section-head">
          <span class="section-icon">{{ currentCfg.icon }}</span>
          <div style="flex:1">
            <div class="section-title">
              {{ currentCfg.title }}
              <span v-if="txnType !== 'all'" class="type-tag">{{ typeLabel }}</span>
            </div>
            <div class="section-subtitle">{{ currentCfg.subtitle }}</div>
          </div>
          <span class="badge-count">
            {{ tierFilter === 'all' ? '100 อันดับแรก' : top100.length + ' รายการ' }}
          </span>
        </div>

        <!-- Note box -->
        <div v-if="currentCfg.note" class="note-box">
          <span style="flex-shrink:0">⚠️</span>
          <span>{{ currentCfg.note }}</span>
        </div>

        <!-- API loading / error states -->
        <div v-if="isApiMode && apiLoading" class="api-state">
          <span class="spinner"></span> กำลังโหลดข้อมูล…
        </div>
        <div v-else-if="isApiMode && apiError" class="api-state api-error">
          ⚠️ {{ apiError }}
        </div>

        <!-- Table -->
        <div v-show="!(isApiMode && (apiLoading || apiError))" ref="scrollBox" class="table-wrap">
          <table>
            <thead>
              <tr>
                <th
v-for="col in currentCfg.cols" :key="col.label"
                    :class="col.cls" :style="col.style">
                  {{ col.label }}
                  <button v-if="col.info" class="col-info-btn" title="คำอธิบาย ความเสี่ยงหลุด" @click.stop="showFrModal = true">ℹ</button>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
v-for="(r, i) in top100"
                  :key="mode === 'interest-ratio' ? r.ticketNo : (r.customerId || r.customerName)"
                  :class="[i < 3 ? 'top3' : '', mode === 'interest-ratio' ? ratioRowClass(r.interestRatioPct) : '']">
                <!-- Rank -->
                <td class="c"><span :class="'rank ' + rankClass(i + 1)">{{ i + 1 }}</span></td>

                <!-- Customer name / chain -->
                <td>
                  <a
class="cust-link" href="#"
                     @click.prevent="openPortfolio(r)">
                    {{ r.customerName || r.cusUid || r.customerId }}
                  </a>
                  <div v-if="mode === 'interest-ratio'" class="chain-id-tag">{{ r.ticketNo || '—' }}</div>
                </td>

                <!-- Tier (existing modes) / Asset type (interest-ratio) -->
                <td v-if="mode !== 'interest-ratio'">
                  <span :class="'tier-badge tier-' + r.tierKey">{{ r.tier }}</span>
                </td>
                <td v-else>
                  <span class="asset-tag">{{ r.assetType || '—' }}</span>
                </td>

                <!-- Revenue mode -->
                <template v-if="mode === 'revenue'">
                  <td class="r">
                    <div class="metric-main tag-green">{{ fmtBaht(r._interest) }}</div>
                    <div class="metric-sub">ดอกเบี้ยสะสม{{ txnType !== 'all' ? ' (ประเภทนี้)' : ' (ทุกตั๋ว)' }}</div>
                  </td>
                  <td class="r">
                    <div class="metric-main">{{ fmtNum(r.totalTransactions) }} ครั้ง</div>
                    <div class="metric-sub">
                      เปิด {{ r.openChains || 0 }} · ปิด {{ r.redeemedChains || 0 }}
                      <span v-if="r.forfeitedChains > 0" class="tag-amber"> · หลุด {{ r.forfeitedChains }}</span>
                    </div>
                  </td>
                  <td class="r col-extra">
                    <span :class="'score-chip ' + chipClass(r.valueFinal)">{{ fmtNum(r.valueFinal, 2) }}</span>
                  </td>
                </template>

                <!-- Forfeited mode -->
                <template v-else-if="mode === 'forfeited'">
                  <td class="r">
                    <div class="metric-main tag-red">{{ fmtNum(r._forfeited) }} ตั๋ว</div>
                    <div class="metric-sub">
                      <span :class="forfeitRateClass(r._forfeitRate)">{{ r._forfeitRate }}%</span>
                      จาก {{ r._total }} ตั๋ว{{ txnType !== 'all' ? 'ประเภทนี้' : 'ทั้งหมด' }}
                    </div>
                  </td>
                  <td class="r">
                    <div class="metric-main tag-red">{{ fmtBaht(r._forfeitedPrincipal) }}</div>
                    <div class="metric-sub">มูลค่าเงินต้น ณ วันหลุด</div>
                  </td>
                  <td class="r col-extra">
                    <span :class="'score-chip ' + chipClass(r.riskFinal)">{{ fmtNum(r.riskFinal, 2) }}</span>
                  </td>
                </template>

                <!-- Forfeit-rate mode -->
                <template v-else-if="mode === 'forfeit-rate'">
                  <td class="r">
                    <div :class="['metric-main', forfeitRateClass(r._forfeitRate)]" style="font-size:1.1rem">{{ r._forfeitRate }}%</div>
                    <div v-if="r._forfeitRate === '100.0'" class="metric-sub tag-red" style="font-weight:700">หลุดทุกตั๋ว{{ txnType !== 'all' ? 'ประเภทนี้' : '' }}</div>
                    <div v-else class="metric-sub tag-muted">สัดส่วนตั๋วหลุด{{ txnType !== 'all' ? ' (ประเภทนี้)' : '' }}</div>
                  </td>
                  <td class="r">
                    <div class="metric-main tag-red">{{ fmtNum(r._forfeited) }} ตั๋ว</div>
                    <div class="metric-sub">จาก {{ r._total }} ตั๋ว{{ txnType !== 'all' ? 'ประเภทนี้' : 'ทั้งหมด' }}</div>
                  </td>
                  <td class="r col-extra">
                    <span :class="'score-chip ' + chipClass(r.riskFinal)">{{ fmtNum(r.riskFinal, 2) }}</span>
                  </td>
                </template>

                <!-- Forfeit-risk forecast mode -->
                <template v-else-if="mode === 'forfeit-risk'">
                  <td class="r">
                    <div class="risk-score-wrap">
                      <div class="risk-bar-bg">
                        <div class="risk-bar-fill" :style="{ width: r._frScore + '%', background: frBarColor(r._frScore) }"></div>
                      </div>
                      <span :class="['metric-main', frScoreClass(r._frScore)]" style="font-size:1.1rem">{{ r._frScore }}%</span>
                    </div>
                    <div class="metric-sub">
                      <span v-if="r.overdueChains > 0" class="tag-red">ค้าง {{ r.overdueChains }} ตั๋ว · {{ r.maxDaysOverdue }} วัน</span>
                      <span v-else class="tag-muted">ไม่มีค้างชำระ</span>
                    </div>
                  </td>
                  <td class="r">
                    <div class="metric-main">{{ fmtBaht(r.overdueChains > 0 ? r.overduePrincipal : r.openPrincipal) }}</div>
                    <div class="metric-sub">
                      {{ r.overdueChains > 0 ? 'เงินต้นค้างชำระ' : 'เงินต้นเปิดอยู่' }}
                      · {{ r.openChains }} ตั๋ว
                    </div>
                  </td>
                  <td class="r col-extra">
                    <div class="fr-tags">
                      <span v-for="tag in r._frReasons" :key="tag.label" :class="'fr-tag fr-tag-' + tag.level">{{ tag.label }}</span>
                    </div>
                  </td>
                  <td class="r">
                    <span :class="'dmi-chip dmi-' + dmiChipClass(r.dmi)">{{ fmtNum(r.dmi, 2) }}</span>
                    <div class="metric-sub">{{ dmiShortLabel(r.dmi) }}</div>
                  </td>
                </template>

                <!-- Principal mode -->
                <template v-else-if="mode === 'principal'">
                  <td class="r">
                    <div class="metric-main">{{ fmtBaht(r._principal) }}</div>
                    <div v-if="r.overdueChains > 0" class="metric-sub tag-red">ค้าง {{ fmtBaht(r.overduePrincipal) }}</div>
                    <div v-else class="metric-sub tag-green">ไม่ค้าง</div>
                  </td>
                  <td class="r">
                    <div class="metric-main">{{ r._open }} ตั๋ว{{ txnType !== 'all' ? 'ประเภทนี้' : '' }}</div>
                    <div v-if="r.overdueChains > 0" class="metric-sub tag-red">ค้าง {{ r.overdueChains }} ใบ · {{ r.maxDaysOverdue }} วัน</div>
                  </td>
                  <td class="r col-extra">
                    <span :class="'score-chip ' + chipClass(r.riskFinal)">{{ fmtNum(r.riskFinal, 2) }}</span>
                  </td>
                </template>

                <!-- Open mode -->
                <template v-else-if="mode === 'open'">
                  <td class="r">
                    <div class="metric-main">{{ r._open }} ตั๋ว{{ txnType !== 'all' ? 'ประเภทนี้' : '' }}</div>
                    <div v-if="r.overdueChains > 0" class="metric-sub tag-red">ค้างชำระ {{ r.overdueChains }} ใบ</div>
                    <div v-else class="metric-sub tag-green">ไม่ค้าง</div>
                  </td>
                  <td class="r">
                    <div class="metric-main">{{ fmtBaht(r._principal) }}</div>
                    <div v-if="r.overdueChains > 0" class="metric-sub tag-red">ค้าง {{ fmtBaht(r.overduePrincipal) }}</div>
                  </td>
                  <td class="r col-extra">
                    <span :class="'score-chip ' + chipClass(r.riskFinal)">{{ fmtNum(r.riskFinal, 2) }}</span>
                  </td>
                </template>

                <!-- Interest-ratio mode -->
                <template v-else-if="mode === 'interest-ratio'">
                  <td class="r">
                    <div class="metric-main">{{ fmtBaht(r.currentPrincipal) }}</div>
                    <div class="metric-sub">ต่อ {{ r.renewalCount }} ครั้ง</div>
                  </td>
                  <td class="r">
                    <div class="metric-main">{{ fmtBaht(r.totalInterestPaid) }}</div>
                    <div class="metric-sub">{{ fmtDate(r.dueDate) }}</div>
                  </td>
                  <td class="r">
                    <div class="ratio-wrap">
                      <div class="ratio-bar-bg">
                        <div
class="ratio-bar-fill"
                             :style="{ width: Math.min(r.interestRatioPct, 100) + '%', background: ratioBarColor(r.interestRatioPct) }">
                        </div>
                      </div>
                      <span :class="['ratio-pct', ratioClass(r.interestRatioPct)]">
                        {{ r.interestRatioPct.toFixed(2) }}%
                      </span>
                    </div>
                    <div v-if="r.interestRatioPct >= 100" class="metric-sub tag-red" style="font-weight:700">ทะลุต้นแล้ว</div>
                    <div v-else class="metric-sub tag-muted">ดอก/เงินต้น</div>
                  </td>
                </template>

              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

  <!-- Forfeit Risk Score Explanation Modal -->
  <Teleport to="body">
    <div v-if="showFrModal" class="fr-modal-overlay" @click.self="showFrModal = false">
      <div class="fr-modal">
        <div class="fr-modal-hdr">
          <div class="fr-modal-title">ความเสี่ยงหลุด — คืออะไร?</div>
          <button class="fr-modal-close" @click="showFrModal = false">✕</button>
        </div>
        <div class="fr-modal-body">
          <p class="fr-modal-lead">
            คะแนน <strong>"ความเสี่ยงหลุด"</strong> เป็น <em>คะแนนระดับลูกค้า</em> ที่ประมาณโอกาสที่ลูกค้ารายนี้จะมีตั๋วหลุดในอนาคต
            คำนวณจาก 5 สัญญาณรวมกัน 0–100 คะแนน ยิ่งสูงยิ่งเสี่ยง
          </p>
          <p class="fr-modal-diff-note">
            ⓘ ต่างจาก <strong>"% โอกาสหลุด"</strong> ในหน้า Portfolio/Forecast ซึ่งเป็นคะแนนระดับตั๋วรายใบ (มีปัจจัยความเร่งด่วนของตั๋วใบนั้นด้วย)
          </p>
          <div class="fr-modal-formula">
            <span class="fr-formula-label">สูตร:</span>
            <code>คะแนน = S1×35% + S2×30% + S3×20% + S4×8% + S5×7%</code>
          </div>
          <div class="fr-modal-factors">
            <div class="fmf-row">
              <div class="fmf-badge fmf-s1">S1</div>
              <div class="fmf-content">
                <div class="fmf-name">ประวัติหลุดในอดีต <span class="fmf-weight">น้ำหนัก 35%</span></div>
                <div class="fmf-desc">สัดส่วนตั๋วที่เคยหลุดต่อตั๋วที่ปิดแล้วทั้งหมดของลูกค้า</div>
              </div>
            </div>
            <div class="fmf-row">
              <div class="fmf-badge fmf-s2">S2</div>
              <div class="fmf-content">
                <div class="fmf-name">สถานะค้างชำระปัจจุบัน <span class="fmf-weight">น้ำหนัก 30%</span></div>
                <div class="fmf-desc">สัดส่วนตั๋วค้างชำระต่อตั๋วเปิดทั้งหมด × จำนวนวันที่เกินกำหนด (สูงสุด 90 วัน)</div>
              </div>
            </div>
            <div class="fmf-row">
              <div class="fmf-badge fmf-s3">S3</div>
              <div class="fmf-content">
                <div class="fmf-name">Risk Score ต่ำ <span class="fmf-weight">น้ำหนัก 20%</span></div>
                <div class="fmf-desc">ลูกค้าที่มี Risk Score ต่ำ = ความน่าเชื่อถือต่ำ = ความเสี่ยงสูง (S3 = 1 − riskFinal/100)</div>
              </div>
            </div>
            <div class="fmf-row">
              <div class="fmf-badge fmf-s4">S4</div>
              <div class="fmf-content">
                <div class="fmf-name">สัญญาณ Early Warning <span class="fmf-weight">น้ำหนัก 8%</span></div>
                <div class="fmf-desc">สัดส่วนเงินต้นค้างชำระต่อเงินต้นเปิดเริ่มสูงขึ้น — สัญญาณเตือนล่วงหน้า</div>
              </div>
            </div>
            <div class="fmf-row" style="border-bottom:none">
              <div class="fmf-badge fmf-s5">S5</div>
              <div class="fmf-content">
                <div class="fmf-name">ต่อดอกช้า <span class="fmf-weight">น้ำหนัก 7%</span></div>
                <div class="fmf-desc">แนวโน้มการมาต่อดอกช้ากว่าปกติในช่วงที่ผ่านมา</div>
              </div>
            </div>
          </div>
          <div class="fr-modal-levels">
            <div class="fml-title">เกณฑ์อ่านค่า</div>
            <div class="fml-row"><span class="fml-dot fml-high"></span><span class="fml-label">เสี่ยงสูง</span><span class="fml-range">≥ 60</span></div>
            <div class="fml-row"><span class="fml-dot fml-mid"></span><span class="fml-label">เสี่ยงปานกลาง</span><span class="fml-range">35 – 59</span></div>
            <div class="fml-row"><span class="fml-dot fml-low"></span><span class="fml-label">เสี่ยงต่ำ</span><span class="fml-range">&lt; 35</span></div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useScoreStore } from '@/stores/score.js'
import { withOrigin } from '@/utils/detailNavigation.js'
import { useRestorableView } from '@/composables/useRestorableView.js'
import { leaderboardApi, reportApi } from '@/utils/scoreSvcApiNew.js'

const route  = useRoute()
const router = useRouter()
const store  = useScoreStore()

const modes = [
  { key: 'open',           label: '🎫 ตั๋วเปิดอยู่มากที่สุด',  colorClass: 'mode-sky' },
  { key: 'principal',      label: '💵 เงินต้นมากที่สุด',       colorClass: 'mode-green' },
  { key: 'revenue',        label: '💰 รายได้มากที่สุด',       colorClass: '' },
  { key: 'interest-ratio', label: '📊 ดอก/ต้น สูงสุด',         colorClass: 'mode-indigo' },
  { key: 'forfeited',      label: '🔴 หลุดจำนำมากที่สุด',     colorClass: 'mode-red' },
  { key: 'forfeit-rate',   label: '📉 % หลุดสูงที่สุด',        colorClass: 'mode-orange' },
  { key: 'forfeit-risk',   label: '⚠️ เสี่ยงหลุด (Forecast)', colorClass: 'mode-rose' },
]

const typeFilters = [
  { key: 'all',      label: 'ทั้งหมด',    colorClass: '' },
  { key: 'Pawn',     label: '🔵 จำนำ',    colorClass: 'mode-blue' },
  { key: 'Renew',    label: '🟢 ต่อดอก',  colorClass: 'mode-emerald' },
  { key: 'Reduce',   label: '🟡 ลดต้น',   colorClass: 'mode-amber' },
  { key: 'Increase', label: '🟣 เพิ่มต้น', colorClass: 'mode-purple' },
]

const tierOptions = [
  { key: 'Platinum', label: 'Platinum',          cssKey: 'tf-platinum' },
  { key: 'Gold',     label: 'Gold',              cssKey: 'tf-gold'     },
  { key: 'Silver',   label: 'Silver',            cssKey: 'tf-silver'   },
  { key: 'Watch',    label: 'ติดตาม',            cssKey: 'tf-watch'    },
  { key: 'Preferred',label: 'ปานกลาง',           cssKey: 'tf-preferred'},
  { key: 'Standard', label: 'ทั่วไป',            cssKey: 'tf-standard' },
  { key: 'Manage',   label: 'ติดตามใกล้ชิด',    cssKey: 'tf-manage'   },
  { key: 'Guard',    label: 'เฝ้าระวัง',         cssKey: 'tf-guard'    },
  { key: 'Caution',  label: 'ระวัง',             cssKey: 'tf-caution'  },
]

const mode       = ref('open')
const txnType    = ref('all')
const tierFilter = ref('all')

// ── Year filter state ──────────────────────────────────────────
const yearFilter     = ref(null)   // null = ทั้งหมด
const availableYears = ref([])
const apiData        = ref([])
const apiLoading     = ref(false)
const apiError       = ref(null)
const showFrModal    = ref(false)

// ── Interest-ratio mode state ──────────────────────────────────
const ratioData      = ref([])

// Filters live in the URL and the scroll offsets in sessionStorage, so clicking a customer and
// coming back with "← กลับ" returns to the table exactly as it was left. It seeds the refs above
// synchronously, and must stay ahead of the loader watcher below so that one fetch goes out with
// the restored filters rather than two.
const { scrollBox } = useRestorableView({
  routeName: 'CsTop100',
  filters: {
    mode: { ref: mode,       default: 'open', values: modes.map(m => m.key) },
    type: { ref: txnType,    default: 'all',  values: typeFilters.map(f => f.key) },
    tier: { ref: tierFilter, default: 'all',  values: ['all', ...tierOptions.map(t => t.key)] },
    year: { ref: yearFilter, default: null,   values: y => y > 1900, type: 'number' },
  },
  // Every part of this gate matters: the whole page body hangs off store.hasData, and the table
  // itself is v-show'd, so rows being in hand is not the same as a table on screen that can hold a
  // scroll offset.
  ready: () => store.hasData && !apiLoading.value && top100.value.length > 0,
})

// The 3 modes that support year filtering
const isYearableMode = computed(() =>
  ['revenue', 'forfeited', 'forfeit-rate'].includes(mode.value)
)

// API-backed: yearable modes + dedicated open-portfolio modes (now backed by APIs
// to cover the full scope, not just loaded pages) + interest-ratio
const isApiMode = computed(() =>
  isYearableMode.value
  || mode.value === 'interest-ratio'
  || ['forfeit-risk', 'principal', 'open'].includes(mode.value)
)

// Type filter is disabled only when the data source doesn't carry per-type breakdowns.
// 'open' and 'principal' return CustomerScore which HAS openByX/principalByX fields → enabled.
const isTypeFilterDisabled = computed(() =>
  isYearableMode.value
  || mode.value === 'interest-ratio'
  || mode.value === 'forfeit-risk'
)

// Lookup store data by public cusUid first; keep customerId/name fallback for older data.
const storeMap = computed(() => {
  const map = new Map()
  store.results.forEach(r => {
    if (r.cusUid) map.set(String(r.cusUid), r)
    if (r.customerId) map.set(String(r.customerId), r)
    if (r.customerName) map.set(String(r.customerName), r)
  })
  return map
})

onMounted(async () => {
  store.init()
  try {
    availableYears.value = await leaderboardApi.years()
  } catch (_e) { /* year fetch non-fatal */ }
})

watch([mode, yearFilter], () => {
  if (mode.value === 'interest-ratio') loadRatioData()
  else if (isApiMode.value) loadApiData()
}, { immediate: true })

async function loadRatioData() {
  apiLoading.value = true
  apiError.value = null
  try {
    ratioData.value = await reportApi.interestRatioTop(100)
  } catch (e) {
    apiError.value = e.message || 'ไม่สามารถโหลดข้อมูลได้'
    ratioData.value = []
  } finally {
    apiLoading.value = false
  }
}

async function loadApiData() {
  apiLoading.value = true
  apiError.value = null
  try {
    // Year-scoped boards take `year`; the three open-portfolio boards take a row cap `n`
    // (NOT `limit` — that silently yields 1 row). See scoreSvcApi.leaderboardApi.
    const callMap = {
      'revenue':      () => leaderboardApi.revenue(yearFilter.value),
      'forfeited':    () => leaderboardApi.forfeitureCount(yearFilter.value),
      'forfeit-rate': () => leaderboardApi.forfeitureRatio(yearFilter.value),
      'forfeit-risk': () => leaderboardApi.forfeitForecast(100),
      'principal':    () => leaderboardApi.openPrincipal(100),
      'open':         () => leaderboardApi.openTickets(100),
    }
    apiData.value = await callMap[mode.value]()
  } catch (e) {
    apiError.value = e.message || 'ไม่สามารถโหลดข้อมูลได้'
    apiData.value = []
  } finally {
    apiLoading.value = false
  }
}

function enrichApiRow(row) {
  // Prefer tier/score fields returned directly from the API (backend-enriched).
  // Fall back to storeMap only if the backend didn't supply them (e.g. old cache entry).
  const lookupKey = row.cusUid || row.customerId
  const s = (row.tier == null && lookupKey != null) ? storeMap.value.get(String(lookupKey)) || {} : {}
  const closed    = row.endedChains || 0
  const forfeited = row.forfeitedChains || 0
  return {
    customerName:        s.customerName || row.customerName || row.cusUid || row.customerId,
    customerId:          row.customerId,
    groupUid:            s.groupUid,
    shopUid:             s.shopUid,
    cusUid:              row.cusUid ?? s.cusUid ?? row.customerId,
    tier:                row.tier    || s.tier    || '—',
    tierKey:             row.tierKey || s.tierKey || 'Standard',
    riskFinal:           row.riskFinal  || s.riskFinal  || 0,
    valueFinal:          row.valueFinal || s.valueFinal || 0,
    totalTransactions:   row.totalTransactions || s.totalTransactions || 0,
    openChains:          row.openChains  || s.openChains  || 0,
    redeemedChains:      closed,
    forfeitedChains:     forfeited,
    _interest:           row.totalInterest || 0,
    _forfeited:          forfeited,
    _forfeitedPrincipal: row.forfeitedPrincipal || s.forfeitedPrincipal || 0,
    _total:              closed,
    _forfeitRate:        closed > 0 ? (forfeited / closed * 100).toFixed(1) : '0.0',
  }
}
function customerRouteId(r) { return store.routeCustomerId(r) }

// Both of the detail pages' inputs ride in history.state, which keeps their URL down to a bare path
// and survives a reload: the customer they show, and — via withOrigin — the list we are leaving, so
// their back buttons return here rather than unwinding the history stack a step at a time.
// selectedCustomerId is in-memory only and is set here just for the pages that still read it.
function openPortfolio(row) {
  const id = customerRouteId(row)
  store.selectedCustomerId = id
  router.push({ name: 'CsPortfolio', state: withOrigin(route, { cus: id }) })
}

const typeLabel = computed(() => typeFilters.find(f => f.key === txnType.value)?.label ?? '')

const MODES_CFG = {
  revenue: {
    icon: '💰', title: 'TOP 100 ลูกค้าที่สร้างรายได้มากที่สุด',
    subtitle: 'เรียงตามดอกเบี้ยสะสม · ข้อมูลจากฐานข้อมูล',
    note: null,
    cols: [
      { label: '#',                  cls: 'c', style: 'width:50px' },
      { label: 'ชื่อลูกค้า',        cls: '' },
      { label: 'ระดับ (ปัจจุบัน)',  cls: '' },
      { label: 'ดอกเบี้ยรวม',       cls: 'r' },
      { label: 'ธุรกรรม',           cls: 'r' },
      { label: 'Value Score',        cls: 'r col-extra' },
    ],
  },
  forfeited: {
    icon: '🔴', title: 'TOP 100 ลูกค้าที่มีการหลุดจำนำมากที่สุด',
    subtitle: 'เรียงตามจำนวนตั๋วที่หลุด · ข้อมูลจากฐานข้อมูล',
    note: null,
    cols: [
      { label: '#',                  cls: 'c', style: 'width:50px' },
      { label: 'ชื่อลูกค้า',        cls: '' },
      { label: 'ระดับ (ปัจจุบัน)',  cls: '' },
      { label: 'จำนวนหลุด',         cls: 'r' },
      { label: 'เงินต้น ณ วันหลุด', cls: 'r' },
      { label: 'Risk Score',         cls: 'r col-extra' },
    ],
  },
  'forfeit-rate': {
    icon: '📉', title: 'TOP 100 ลูกค้าที่มีสัดส่วนหลุดจำนำสูงที่สุด',
    subtitle: 'เรียงตาม % ตั๋วหลุด/ตั๋วปิด · เฉพาะลูกค้าที่มี ≥1 ตั๋วปิด · ข้อมูลจากฐานข้อมูล',
    note: null,
    cols: [
      { label: '#',                  cls: 'c', style: 'width:50px' },
      { label: 'ชื่อลูกค้า',        cls: '' },
      { label: 'ระดับ (ปัจจุบัน)',  cls: '' },
      { label: '% หลุด',            cls: 'r' },
      { label: 'หลุด / ทั้งหมด',   cls: 'r' },
      { label: 'Risk Score',         cls: 'r col-extra' },
    ],
  },
  'forfeit-risk': {
    icon: '⚠️', title: 'TOP 100 ลูกค้าที่มีความเสี่ยงหลุดจำนำสูงที่สุด',
    subtitle: 'เรียงตาม Forfeit Risk Score — ประมาณการจากประวัติ+สถานะปัจจุบัน+ทิศทาง Risk · เฉพาะลูกค้าที่มีตั๋วเปิดอยู่',
    note: 'คะแนนความเสี่ยงคำนวณจาก: อัตราหลุดอดีต (35%) + ค้างชำระ/วัน (30%) + Risk Score ต่ำ (20%) + สัญญาณ Early Warning+ต่อดอกช้า (15%) · ยิ่งสูงยิ่งเสี่ยง',
    cols: [
      { label: '#',                  cls: 'c', style: 'width:50px' },
      { label: 'ชื่อลูกค้า',        cls: '' },
      { label: 'ระดับ',             cls: '' },
      { label: 'ความเสี่ยงหลุด',    cls: 'r', info: true },
      { label: 'เงินต้นเสี่ยง',     cls: 'r' },
      { label: 'เหตุผลหลัก',        cls: 'r col-extra' },
      { label: 'DMI',                cls: 'r' },
    ],
  },
  principal: {
    icon: '💵', title: 'TOP 100 ลูกค้าที่มีมูลค่าเงินต้นมากที่สุด',
    subtitle: 'เรียงตามยอดเงินต้นรวมตั๋วที่เปิดอยู่ในปัจจุบัน',
    note: null,
    cols: [
      { label: '#',                  cls: 'c', style: 'width:50px' },
      { label: 'ชื่อลูกค้า',        cls: '' },
      { label: 'ระดับ',             cls: '' },
      { label: 'เงินต้น (เปิดอยู่)', cls: 'r' },
      { label: 'ตั๋ว / ค้างชำระ',  cls: 'r' },
      { label: 'Risk Score',         cls: 'r col-extra' },
    ],
  },
  open: {
    icon: '🎫', title: 'TOP 100 ลูกค้าที่มีตั๋วที่ยังเปิดอยู่มากที่สุด',
    subtitle: 'เรียงตามจำนวนตั๋ว Active ณ ปัจจุบัน',
    note: null,
    cols: [
      { label: '#',                  cls: 'c', style: 'width:50px' },
      { label: 'ชื่อลูกค้า',        cls: '' },
      { label: 'ระดับ',             cls: '' },
      { label: 'ตั๋วเปิดอยู่',      cls: 'r' },
      { label: 'เงินต้น / ค้างชำระ', cls: 'r' },
      { label: 'Risk Score',         cls: 'r col-extra' },
    ],
  },
  'interest-ratio': {
    icon: '📊', title: 'TOP 100 ตั๋วที่ส่งดอกเบี้ยสูงเทียบเงินต้น',
    subtitle: 'เรียงตามสัดส่วนดอกเบี้ยสะสม ÷ เงินต้น · เฉพาะตั๋วที่ยังเปิดอยู่ · ข้อมูลจากฐานข้อมูล',
    note: 'รายการนี้แสดงผลต่อตั๋ว (chain) ไม่ใช่ต่อลูกค้า · ตั๋วสีแดง = ดอกสะสมทะลุเงินต้นแล้ว',
    cols: [
      { label: '#',                  cls: 'c', style: 'width:50px' },
      { label: 'ลูกค้า / เลขตั๋ว', cls: '' },
      { label: 'สินทรัพย์',         cls: '' },
      { label: 'เงินต้น',           cls: 'r' },
      { label: 'ดอกสะสม',           cls: 'r' },
      { label: 'ดอก ÷ ต้น',         cls: 'r' },
    ],
  },
}

const currentCfg = computed(() => {
  const cfg = MODES_CFG[mode.value]
  if (!isApiMode.value || ['forfeit-risk', 'principal', 'open'].includes(mode.value)) return cfg
  const yearLabel = yearFilter.value ? `ปี ${yearFilter.value}` : 'ทุกปี'
  return { ...cfg, subtitle: `${yearLabel} · ${cfg.subtitle}` }
})

// Resolve type-filtered fields for a given item
function typeFields(item) {
  const t = txnType.value
  if (t === 'all') {
    const total = (item.redeemedChains || 0) + (item.forfeitedChains || 0) + (item.openChains || 0)
    const forfeitRate = total > 0 ? ((item.forfeitedChains || 0) / total * 100).toFixed(1) : '0.0'
    return {
      _interest:          item.totalInterest || 0,
      _open:              item.openChains || 0,
      _principal:         item.openPrincipal || 0,
      _forfeited:         item.forfeitedChains || 0,
      _forfeitedPrincipal:item.forfeitedPrincipal || 0,
      _closed:            item.redeemedChains || 0,
      _total:             total,
      _forfeitRate:       forfeitRate,
    }
  }
  const open      = item[`openBy${t}`] || 0
  const principal = item[`principalBy${t}`] || 0
  const forfeited = item[`forfeitedBy${t}`] || 0
  const forfeitedPrincipal = item[`forfeitedPrincipalBy${t}`] || 0
  const closed    = item[`redeemedBy${t}`] || 0
  const interest  = item[`interestBy${t}`] || 0
  const total     = open + closed + forfeited
  const forfeitRate = total > 0 ? (forfeited / total * 100).toFixed(1) : '0.0'
  return { _interest: interest, _open: open, _principal: principal,
           _forfeited: forfeited, _forfeitedPrincipal: forfeitedPrincipal,
           _closed: closed, _total: total, _forfeitRate: forfeitRate }
}

const top100 = computed(() => {
  // Interest-ratio mode: per-chain data from dedicated API (no tier concept)
  if (mode.value === 'interest-ratio') {
    return ratioData.value
  }

  const tf = tierFilter.value
  const byTier = arr => tf === 'all' ? arr : arr.filter(r => r.tierKey === tf)

  // Open-portfolio API modes: API returns full ScoreResponse — no enrichment needed
  if (mode.value === 'forfeit-risk') {
    return byTier(apiData.value.map(item => {
      const { score, reasons } = computeForfeitRisk(item)
      return { ...item, ...typeFields(item), _frScore: score, _frReasons: reasons }
    }))
  }
  if (mode.value === 'principal' || mode.value === 'open') {
    const rows = byTier(apiData.value.map(item => ({ ...item, ...typeFields(item) })))
    if (mode.value === 'open') {
      return rows.filter(r => r._open > 0).sort((a, b) => b._open - a._open || b._principal - a._principal)
    }
    return rows.filter(r => r._principal > 0).sort((a, b) => b._principal - a._principal)
  }

  // Yearable API modes: LeaderboardEntry — enrich tier/score from store
  if (isApiMode.value) {
    return byTier(apiData.value.map(enrichApiRow))
  }

  // Store-backed modes: apply tier filter BEFORE slice → true TOP 100 within tier
  const enriched = byTier(store.results.map(item => {
    const { score, reasons } = computeForfeitRisk(item)
    return { ...item, ...typeFields(item), _frScore: score, _frReasons: reasons }
  }))

  if (mode.value === 'forfeit-risk') {
    return enriched
      .filter(r => r.openChains > 0)
      .sort((a, b) => b._frScore - a._frScore || b.openPrincipal - a.openPrincipal)
      .slice(0, 100)
  }

  if (mode.value === 'forfeit-rate') {
    return enriched
      .filter(r => r._forfeited > 0)
      .sort((a, b) => {
        const diff = parseFloat(b._forfeitRate) - parseFloat(a._forfeitRate)
        return diff !== 0 ? diff : b._forfeited - a._forfeited
      })
      .slice(0, 100)
  }

  if (mode.value === 'forfeited') {
    return enriched
      .filter(r => r._forfeited > 0)
      .sort((a, b) => b._forfeited - a._forfeited || b._forfeitedPrincipal - a._forfeitedPrincipal)
      .slice(0, 100)
  }

  if (mode.value === 'revenue') {
    return enriched
      .filter(r => r._interest > 0)
      .sort((a, b) => b._interest - a._interest)
      .slice(0, 100)
  }

  if (mode.value === 'principal') {
    return enriched
      .filter(r => r._principal > 0)
      .sort((a, b) => b._principal - a._principal)
      .slice(0, 100)
  }

  // open
  return enriched
    .filter(r => r._open > 0)
    .sort((a, b) => b._open - a._open || b._principal - a._principal)
    .slice(0, 100)
})

function computeForfeitRisk(r) {
  const totalClosed = (r.forfeitedChains || 0) + (r.redeemedChains || 0)
  const historicalRate = totalClosed > 0 ? (r.forfeitedChains || 0) / totalClosed : 0

  const overdueRatio  = r.openChains > 0 ? (r.overdueChains || 0) / r.openChains : 0
  const overdueSignal = overdueRatio * Math.min((r.maxDaysOverdue || 0) / 90, 1)

  const riskSignal = (100 - (r.riskFinal || 0)) / 100

  const warnSignal = (r.earlyWarningAdj || 0) < -10 ? 1 : (r.earlyWarningAdj || 0) < 0 ? 0.5 : 0
  const lateSignal = (r.lateRenewalTrendAdj || 0) < -5 ? 1 : (r.lateRenewalTrendAdj || 0) < 0 ? 0.5 : 0

  const score = Math.round(Math.min(100,
    historicalRate * 35 +
    overdueSignal  * 30 +
    riskSignal     * 20 +
    warnSignal     *  8 +
    lateSignal     *  7
  ))

  const reasons = []
  if ((r.overdueChains || 0) > 0)   reasons.push({ label: 'ค้างชำระ',     level: 'high' })
  if (historicalRate >= 0.3)         reasons.push({ label: 'ประวัติหลุดสูง', level: 'high' })
  else if (historicalRate >= 0.1)    reasons.push({ label: 'มีประวัติหลุด', level: 'mid' })
  if (riskSignal >= 0.67)            reasons.push({ label: 'Risk ต่ำมาก',    level: 'high' })
  else if (riskSignal >= 0.5)        reasons.push({ label: 'Risk ต่ำ',       level: 'mid' })
  if ((r.earlyWarningAdj || 0) < 0)  reasons.push({ label: 'Early Warning', level: 'mid' })
  if ((r.lateRenewalTrendAdj||0)<0)  reasons.push({ label: 'ต่อดอกช้า',     level: 'mid' })

  return { score, reasons: reasons.slice(0, 3) }
}

function fmtNum(n, dec = 0) {
  return Number(n).toLocaleString('th-TH', { minimumFractionDigits: dec, maximumFractionDigits: dec })
}
function fmtBaht(n) {
  if (n === null || n === undefined || n === 0) return '฿0'
  const v = Number(n)
  if (v >= 1000000) return '฿' + fmtNum(v / 1000000, 2) + 'M'
  if (v >= 100000)  return '฿' + fmtNum(v / 1000, 1) + 'K'
  return '฿' + fmtNum(v)
}

function ratioClass(pct)     { return pct >= 100 ? 'tag-red' : pct >= 75 ? 'tag-amber' : pct >= 50 ? 'tag-green' : 'tag-muted' }
function ratioBarColor(pct) { return pct >= 100 ? '#ef4444' : pct >= 75 ? '#f59e0b' : pct >= 50 ? '#10b981' : '#94a3b8' }
function ratioRowClass(pct) { return pct >= 100 ? 'ratio-row-red' : pct >= 75 ? 'ratio-row-orange' : '' }
function fmtDate(d) {
  if (!d) return '—'
  const dt = new Date(d)
  return dt.toLocaleDateString('th-TH', { year: '2-digit', month: 'short', day: 'numeric' })
}
function rankClass(n)       { return n === 1 ? 'r1' : n === 2 ? 'r2' : n === 3 ? 'r3' : 'rn' }
function chipClass(val)     { return val >= 67 ? 'chip-high' : val >= 34 ? 'chip-mid' : 'chip-low' }
function forfeitRateClass(r){ const v = parseFloat(r); return v >= 30 ? 'tag-red' : v >= 15 ? 'tag-amber' : 'tag-green' }
function frScoreClass(s)    { return s >= 60 ? 'tag-red' : s >= 35 ? 'tag-amber' : 'tag-green' }
function frBarColor(s)      { return s >= 60 ? '#ef4444' : s >= 35 ? '#f59e0b' : '#10b981' }
function dmiChipClass(d)    { return d >= 70 ? 'high' : d >= 30 ? 'mid' : 'low' }
function dmiShortLabel(d)   { return d >= 70 ? 'ดี' : d >= 50 ? 'พอใช้' : d >= 30 ? 'ปานกลาง' : 'ต่ำ' }
</script>

<style scoped>
/* ── Page wrapper ────────────────────────────────────────────── */
.page-wrap { width: 100%; padding: 16px 24px 40px; }

/* ── No data state ───────────────────────────────────────────── */
.state-no-data { text-align: center; padding: 80px 24px; color: #6b7280; }
.nd-icon { font-size: 3rem; margin-bottom: 12px; }
.state-no-data h2 { font-size: 1.1rem; margin-bottom: 8px; color: #1a1d23; }
.state-no-data p  { font-size: .85rem; line-height: 1.6; }
.btn-upload {
  display: inline-block; margin-top: 18px; padding: 9px 22px;
  background: #1d4ed8; color: #fff; border-radius: 8px;
  text-decoration: none; font-weight: 600; font-size: .88rem;
}
.btn-upload:hover { background: #1e40af; }

/* ── Filter bar (2 rows) ─────────────────────────────────────── */
.filter-bar {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,.08), 0 4px 16px rgba(0,0,0,.06);
  padding: 10px 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.filter-row {
  display: flex; gap: 8px; flex-wrap: wrap; align-items: center;
}
.filter-row-type {
  padding-top: 8px;
  border-top: 1px solid #f0f2f5;
}
.filter-label {
  font-size: .72rem; font-weight: 700; color: #6b7280;
  text-transform: uppercase; letter-spacing: .5px;
  white-space: nowrap; margin-right: 4px; min-width: 62px;
}
.mode-btn {
  padding: 7px 16px; border-radius: 8px; font-size: .86rem; font-weight: 600;
  border: 1px solid #e2e6ea; background: #f8f9fb; color: #6b7280;
  cursor: pointer; transition: all .15s; white-space: nowrap;
}
.mode-btn:hover { border-color: #93c5fd; color: #1d4ed8; background: #eff6ff; }
.mode-btn.active           { background: #1d4ed8; color: #fff; border-color: #1d4ed8; box-shadow: 0 2px 6px rgba(29,78,216,.35); }
.mode-btn.mode-red.active  { background: #dc2626; border-color: #dc2626; box-shadow: 0 2px 6px rgba(220,38,38,.35); }
.mode-btn.mode-green.active{ background: #059669; border-color: #059669; box-shadow: 0 2px 6px rgba(5,150,105,.35); }
.mode-btn.mode-sky.active    { background: #0284c7; border-color: #0284c7; box-shadow: 0 2px 6px rgba(2,132,199,.35); }
.mode-btn.mode-orange.active  { background: #ea580c; border-color: #ea580c; box-shadow: 0 2px 6px rgba(234,88,12,.35); }
.mode-btn.mode-blue.active    { background: #3b82f6; border-color: #3b82f6; box-shadow: 0 2px 6px rgba(59,130,246,.35); }
.mode-btn.mode-emerald.active { background: #10b981; border-color: #10b981; box-shadow: 0 2px 6px rgba(16,185,129,.35); }
.mode-btn.mode-amber.active   { background: #d97706; border-color: #d97706; box-shadow: 0 2px 6px rgba(217,119,6,.35); }
.mode-btn.mode-purple.active  { background: #7c3aed; border-color: #7c3aed; box-shadow: 0 2px 6px rgba(124,58,237,.35); }

/* ── Year filter button (smaller) ───────────────────────────── */
.year-btn { padding: 5px 12px; font-size: .8rem; }

/* ── Disabled type filter (when year is selected) ────────────── */
.disabled-btn { opacity: .35; cursor: not-allowed; pointer-events: none; }
.filter-hint  { font-size: .72rem; color: #9ca3af; font-style: italic; }

/* ── API loading / error ─────────────────────────────────────── */
.api-state {
  padding: 32px 20px; text-align: center;
  font-size: .88rem; color: #6b7280;
  display: flex; align-items: center; justify-content: center; gap: 10px;
}
.api-error { color: #dc2626; }
.spinner {
  display: inline-block; width: 18px; height: 18px; flex-shrink: 0;
  border: 2px solid #e5e7eb; border-top-color: #1d4ed8;
  border-radius: 50%; animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Section card ────────────────────────────────────────────── */
.section-card { background: #fff; border-radius: 10px; box-shadow: 0 1px 4px rgba(0,0,0,.08), 0 4px 16px rgba(0,0,0,.06); overflow: hidden; }
.section-head { display: flex; align-items: center; gap: 12px; padding: 14px 20px; border-bottom: 1px solid #e2e6ea; flex-wrap: wrap; }
.section-icon    { font-size: 1.4rem; flex-shrink: 0; }
.section-title   { font-size: 1rem; font-weight: 700; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.section-subtitle{ font-size: .75rem; color: #6b7280; margin-top: 2px; }
.badge-count { background: #f0f2f5; border: 1px solid #e2e6ea; color: #6b7280; border-radius: 12px; padding: 3px 10px; font-size: .72rem; font-weight: 600; white-space: nowrap; }
.type-tag { background: #eff6ff; border: 1px solid #bfdbfe; color: #1d4ed8; border-radius: 6px; padding: 1px 8px; font-size: .78rem; font-weight: 600; }

/* ── Note box ────────────────────────────────────────────────── */
.note-box { background: #fffbeb; border-bottom: 1px solid #fde68a; padding: 9px 20px; font-size: .78rem; color: #92400e; display: flex; gap: 8px; align-items: flex-start; }

/* ── Table ───────────────────────────────────────────────────── */
.table-wrap { overflow-x: auto; overflow-y: auto; max-height: 1320px; }
table { width: 100%; border-collapse: collapse; line-height: 1.55; }
thead th {
  background: #f8f9fb; padding: 10px 16px; text-align: left;
  font-size: .68rem; font-weight: 700; color: #6b7280;
  text-transform: uppercase; letter-spacing: .5px; border-bottom: 1px solid #e2e6ea;
  position: sticky; top: 0; z-index: 1;
}
thead th.r { text-align: right; }
thead th.c { text-align: center; }
tbody tr { border-bottom: 1px solid #f3f4f6; transition: background .12s; }
tbody tr:last-child { border-bottom: none; }
tbody tr:hover { background: #f7f9ff; }
tbody tr.top3 { background: #fffbf0; }
tbody tr.top3:hover { background: #fff5e0; }
td { padding: 12px 16px; font-size: .9rem; vertical-align: middle; line-height: 1.55; }
td.r { text-align: right; }
td.c { text-align: center; }

@media (max-width: 768px) { .col-extra { display: none; } }

/* ── Rank badge ──────────────────────────────────────────────── */
.rank { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 7px; font-size: .8rem; font-weight: 800; }
.r1 { background: #fbbf24; color: #78350f; }
.r2 { background: #cbd5e1; color: #334155; }
.r3 { background: #d4a574; color: #7c2d12; }
.rn { background: #f1f5f9; color: #94a3b8; font-size: .75rem; }

/* ── Tier badge ──────────────────────────────────────────────── */
.tier-badge    { display: inline-flex; align-items: center; padding: 2px 9px; border-radius: 20px; font-size: .78rem; font-weight: 700; white-space: nowrap; }
.tier-Platinum { background: #ede9fe; color: #6d28d9; }
.tier-Gold     { background: #fef9c3; color: #92400e; }
.tier-Silver   { background: #f1f5f9; color: #475569; }
.tier-Watch    { background: #dbeafe; color: #1d4ed8; }
.tier-Preferred{ background: #e0f2fe; color: #075985; }
.tier-Standard { background: #eff6ff; color: #1e40af; }
.tier-Manage   { background: #fee2e2; color: #b91c1c; }
.tier-Guard    { background: #fef3c7; color: #92400e; }
.tier-Caution  { background: #fde8e8; color: #991b1b; }

/* ── Metric ──────────────────────────────────────────────────── */
.metric-main { font-size: .97rem; font-weight: 700; }
.metric-sub  { font-size: .73rem; color: #6b7280; margin-top: 2px; }
.tag-red   { color: #dc2626; font-weight: 600; }
.tag-amber { color: #d97706; font-weight: 600; }
.tag-green { color: #059669; font-weight: 600; }
.tag-muted { color: #94a3b8; }

/* ── Score chip ──────────────────────────────────────────────── */
.score-chip { display: inline-block; padding: 2px 8px; border-radius: 6px; font-size: .78rem; font-weight: 700; }
.chip-high { background: #d1fae5; color: #065f46; }
.chip-mid  { background: #fef3c7; color: #92400e; }
.chip-low  { background: #fee2e2; color: #991b1b; }

/* ── Customer link ───────────────────────────────────────────── */
.cust-link { color: #1d4ed8; text-decoration: none; font-weight: 600; line-height: 1.55; }
.cust-link:hover { text-decoration: underline; }

/* ── Forfeit-risk mode ───────────────────────────────────────── */
.mode-btn.mode-rose.active { background: #e11d48; border-color: #e11d48; box-shadow: 0 2px 6px rgba(225,29,72,.35); }
.risk-score-wrap { display: flex; align-items: center; gap: 8px; justify-content: flex-end; }
.risk-bar-bg { width: 70px; height: 6px; background: #f3f4f6; border-radius: 3px; overflow: hidden; flex-shrink: 0; }
.risk-bar-fill { height: 100%; border-radius: 3px; transition: width .3s; }
.fr-tags { display: flex; flex-direction: column; gap: 3px; align-items: flex-end; }
.fr-tag { display: inline-block; padding: 1px 7px; border-radius: 4px; font-size: .68rem; font-weight: 700; white-space: nowrap; }
.fr-tag-high { background: #fee2e2; color: #b91c1c; }
.fr-tag-mid  { background: #fef3c7; color: #92400e; }
.dmi-chip { display: inline-block; padding: 2px 8px; border-radius: 6px; font-size: .78rem; font-weight: 700; }
.dmi-high { background: #ede9fe; color: #5b21b6; }
.dmi-mid  { background: #fef3c7; color: #92400e; }
.dmi-low  { background: #fee2e2; color: #991b1b; }

/* ── Interest-ratio mode ─────────────────────────────────────── */
.mode-btn.mode-indigo.active { background: #4f46e5; border-color: #4f46e5; box-shadow: 0 2px 6px rgba(79,70,229,.35); }

.chain-id-tag {
  font-size: .7rem; color: #94a3b8; margin-top: 2px;
  font-family: ui-monospace, monospace;
}
.asset-tag {
  display: inline-block; padding: 2px 8px; border-radius: 6px;
  background: #f0fdf4; color: #166534; font-size: .78rem; font-weight: 600;
}

/* Ratio progress bar */
.ratio-wrap {
  display: flex; align-items: center; gap: 8px; justify-content: flex-end;
}
.ratio-bar-bg {
  width: 64px; height: 6px; background: #f1f5f9;
  border-radius: 3px; overflow: hidden; flex-shrink: 0;
}
.ratio-bar-fill { height: 100%; border-radius: 3px; transition: width .3s; }
.ratio-pct { font-size: .97rem; font-weight: 700; min-width: 48px; text-align: right; }

/* Row highlight for high-ratio tickets */
tbody tr.ratio-row-red   { background: #fff5f5; }
tbody tr.ratio-row-red:hover { background: #fee2e2; }
tbody tr.ratio-row-orange { background: #fffbf0; }
tbody tr.ratio-row-orange:hover { background: #fef3c7; }

/* ── Tier filter buttons ─────────────────────────────────────── */
.tier-btn { padding: 5px 12px; font-size: .8rem; }
.mode-btn.tf-platinum.active { background: #6d28d9; color: #fff; border-color: #6d28d9; box-shadow: 0 2px 6px rgba(109,40,217,.35); }
.mode-btn.tf-gold.active     { background: #b45309; color: #fff; border-color: #b45309; box-shadow: 0 2px 6px rgba(180,83,9,.35); }
.mode-btn.tf-silver.active   { background: #475569; color: #fff; border-color: #475569; box-shadow: 0 2px 6px rgba(71,85,105,.35); }
.mode-btn.tf-watch.active    { background: #1d4ed8; color: #fff; border-color: #1d4ed8; box-shadow: 0 2px 6px rgba(29,78,216,.35); }
.mode-btn.tf-preferred.active{ background: #075985; color: #fff; border-color: #075985; box-shadow: 0 2px 6px rgba(7,89,133,.35); }
.mode-btn.tf-standard.active { background: #1e40af; color: #fff; border-color: #1e40af; box-shadow: 0 2px 6px rgba(30,64,175,.35); }
.mode-btn.tf-manage.active   { background: #b91c1c; color: #fff; border-color: #b91c1c; box-shadow: 0 2px 6px rgba(185,28,28,.35); }
.mode-btn.tf-guard.active    { background: #92400e; color: #fff; border-color: #92400e; box-shadow: 0 2px 6px rgba(146,64,14,.35); }
.mode-btn.tf-caution.active  { background: #991b1b; color: #fff; border-color: #991b1b; box-shadow: 0 2px 6px rgba(153,27,27,.35); }

/* ── Col info button ─────────────────────────────────────────── */
.col-info-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 15px; height: 15px; border-radius: 50%;
  background: #dbeafe; color: #1d4ed8; border: none;
  font-size: .6rem; font-weight: 700; cursor: pointer;
  margin-left: 5px; vertical-align: middle; line-height: 1;
  transition: background .15s, color .15s; flex-shrink: 0;
}
.col-info-btn:hover { background: #1d4ed8; color: #fff; }

/* ── Forfeit Risk Modal ───────────────────────────────────────── */
.fr-modal-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(15,23,42,.55); backdrop-filter: blur(2px);
  display: flex; align-items: center; justify-content: center; padding: 24px;
}
.fr-modal {
  background: #fff; border-radius: 14px; width: 100%; max-width: 540px;
  box-shadow: 0 8px 40px rgba(0,0,0,.22); overflow: hidden;
}
.fr-modal-hdr {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px 14px;
  background: linear-gradient(135deg,#7f1d1d 0%,#dc2626 100%);
}
.fr-modal-title { font-size: .95rem; font-weight: 700; color: #fff; }
.fr-modal-close {
  background: rgba(255,255,255,.18); border: none; color: #fff;
  border-radius: 6px; width: 28px; height: 28px; cursor: pointer;
  font-size: .85rem; display: flex; align-items: center; justify-content: center;
  transition: background .15s;
}
.fr-modal-close:hover { background: rgba(255,255,255,.35); }
.fr-modal-body { padding: 18px 20px 20px; }
.fr-modal-lead { font-size: .84rem; color: #374151; line-height: 1.6; margin: 0 0 10px; }
.fr-modal-lead strong { color: #111827; }
.fr-modal-lead em { font-style: normal; font-weight: 700; color: #b91c1c; }
.fr-modal-diff-note {
  font-size: .76rem; color: #92400e; background: #fef3c7;
  border: 1px solid #fde68a; border-radius: 7px;
  padding: 8px 12px; margin-bottom: 12px; line-height: 1.5;
}
.fr-modal-formula {
  background: #fff5f5; border: 1px solid #fecaca; border-radius: 8px;
  padding: 10px 14px; margin-bottom: 14px;
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
}
.fr-formula-label { font-size: .7rem; font-weight: 700; color: #b91c1c; text-transform: uppercase; letter-spacing: .4px; white-space: nowrap; }
.fr-modal-formula code { font-family: monospace; font-size: .83rem; font-weight: 700; color: #7f1d1d; background: none; padding: 0; }
.fr-modal-factors { border: 1px solid #e5e7eb; border-radius: 9px; overflow: hidden; margin-bottom: 14px; }
.fmf-row { display: flex; align-items: flex-start; gap: 12px; padding: 11px 14px; border-bottom: 1px solid #f3f4f6; }
.fmf-badge { width: 28px; height: 28px; border-radius: 7px; font-size: .7rem; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
.fmf-s1 { background: #fee2e2; color: #b91c1c; }
.fmf-s2 { background: #fef3c7; color: #92400e; }
.fmf-s3 { background: #fef3c7; color: #78350f; }
.fmf-s4 { background: #fff7ed; color: #c2410c; }
.fmf-s5 { background: #eff6ff; color: #1d4ed8; }
.fmf-content { flex: 1; min-width: 0; }
.fmf-name { font-size: .82rem; font-weight: 700; color: #111827; margin-bottom: 3px; }
.fmf-weight { font-size: .72rem; font-weight: 600; color: #6b7280; margin-left: 6px; }
.fmf-desc { font-size: .76rem; color: #6b7280; line-height: 1.5; }
.fr-modal-levels { background: #fff5f5; border: 1px solid #fecaca; border-radius: 8px; padding: 11px 14px; }
.fml-title { font-size: .65rem; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 8px; }
.fml-row { display: flex; align-items: center; gap: 8px; margin-bottom: 5px; }
.fml-row:last-child { margin-bottom: 0; }
.fml-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.fml-high { background: #dc2626; }
.fml-mid  { background: #d97706; }
.fml-low  { background: #059669; }
.fml-label { font-size: .8rem; font-weight: 600; color: #374151; flex: 1; }
.fml-range { font-size: .78rem; font-weight: 700; color: #6b7280; font-family: monospace; }
</style>
